import { FormDialog, FormDialogContent, FormDialogHeader, FormDialogTitle, FormDialogBody, FormDialogFooter } from '../../components/ui/form-dialog';
// apps/web/src/components/users/user-dialog.tsx — Fase 01, Plano 62 (USR-01/USR-02).
// Mutação administrativa via apiMutate (cookie + x-csrf-token). O select de
// perfil nasce de GET /authorization/roles — sem UUID literal no JSX.
import { useEffect, useState, type FormEvent, type ReactElement } from 'react';
import { apiMutate } from '../../lib/csrf-fetch';
import { avisoTrocaDePerfil } from '../perfis/comum';
import type { UserRow } from './user-table';
import { CampoSelect } from '../ui/campo-select';

export interface UserDialogProps {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly onUserCreated: () => void;
  /** Quando presente, o diálogo entra em modo EDIÇÃO (PATCH) desse usuário. */
  readonly editUser?: UserRow | null;
}

interface RoleOption {
  readonly id: string;
  readonly name: string;
}

const OPERATOR_ROLE_NAME = 'Operador';
const SERVER_CREATE_ERROR =
  'Não foi possível criar o usuário. Nenhum dado foi salvo — tente novamente.';
const DUPLICATE_EMAIL_ERROR = 'Já existe um usuário com este e-mail.';

function isDuplicateEmailMessage(message: string): boolean {
  return /e-?mail/i.test(message);
}

export function UserDialog({ open, onClose, onUserCreated, editUser }: UserDialogProps): ReactElement | null {
  const isEdit = Boolean(editUser);
  const [username, setUsername] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [email, setEmail] = useState('');
  const [roleId, setRoleId] = useState('');
  const [roles, setRoles] = useState<readonly RoleOption[]>([]);
  const [rolesLoading, setRolesLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [setupLink, setSetupLink] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    // Prefill em modo edição (o username é imutável).
    if (editUser) {
      setUsername(editUser.username);
      setDisplayName(editUser.displayName);
      setEmail(editUser.email ?? '');
    }
    let active = true;
    async function loadRoles(): Promise<void> {
      setRolesLoading(true);
      try {
        const res = await fetch('/api/v1/authorization/roles', { credentials: 'include' });
        if (!res.ok) {
          throw new Error('Não foi possível carregar os perfis.');
        }
        const data = (await res.json()) as RoleOption[];
        if (!active) return;
        setRoles(data);
        if (editUser) {
          setRoleId(editUser.role.id);
        } else {
          const operador = data.find((role) => role.name === OPERATOR_ROLE_NAME);
          setRoleId(operador?.id ?? data[0]?.id ?? '');
        }
      } catch (err) {
        if (active) {
          setError(err instanceof Error ? err.message : 'Não foi possível carregar os perfis.');
        }
      } finally {
        if (active) setRolesLoading(false);
      }
    }
    void loadRoles();
    return () => {
      active = false;
    };
  }, [open, editUser]);

  if (!open) return null;

  const handleSubmit = async (e?: FormEvent) => {
    e?.preventDefault();
    setSubmitting(true);
    setError(null);
    setEmailError(null);
    try {
      if (isEdit && editUser) {
        const res = await apiMutate(`/api/v1/users/${editUser.id}`, {
          method: 'PATCH',
          body: { displayName, email: email.trim().length > 0 ? email : null, roleId },
        });
        if (!res.ok) {
          const payload = (await res.json().catch(() => ({}))) as { message?: string };
          const message = payload.message ?? SERVER_CREATE_ERROR;
          if (res.status === 409 && isDuplicateEmailMessage(message)) {
            setEmailError(DUPLICATE_EMAIL_ERROR);
          } else if (res.status >= 500) {
            setError(SERVER_CREATE_ERROR);
          } else {
            setError(message);
          }
          return;
        }
        onUserCreated();
        handleClose();
        return;
      }
      const res = await apiMutate('/api/v1/users', {
        method: 'POST',
        body: {
          username,
          displayName,
          email: email.trim().length > 0 ? email : null,
          roleId,
        },
      });
      if (!res.ok) {
        const payload = (await res.json().catch(() => ({}))) as { message?: string };
        const message = payload.message ?? SERVER_CREATE_ERROR;
        if (res.status === 409 && isDuplicateEmailMessage(message)) {
          setEmailError(DUPLICATE_EMAIL_ERROR);
        } else if (res.status >= 500) {
          setError(SERVER_CREATE_ERROR);
        } else {
          setError(message);
        }
        return;
      }
      const data = (await res.json()) as { setupLink: string };
      setSetupLink(data.setupLink);
      onUserCreated();
    } catch {
      setError(SERVER_CREATE_ERROR);
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setUsername('');
    setDisplayName('');
    setEmail('');
    setRoleId('');
    setRoles([]);
    setError(null);
    setEmailError(null);
    setSetupLink(null);
    onClose();
  };

  return (
    <FormDialog open={open} onOpenChange={(nextOpen) => { if (!nextOpen) handleClose(); }}>
      <FormDialogContent aria-describedby={undefined}>
        <FormDialogHeader><FormDialogTitle>{isEdit ? 'Editar Usuário' : 'Novo Usuário'}</FormDialogTitle></FormDialogHeader>

        {setupLink ? (
          <FormDialogBody className="space-y-4">
            <div className="p-3 bg-green-50 border border-green-200 rounded text-sm text-green-800">
              Usuário criado com sucesso! Forneça o link de definição de senha (uso único):
            </div>
            <div className="p-2 bg-slate-100 rounded font-mono text-xs break-all select-all text-slate-800">
              {setupLink}
            </div>
            <button
              type="button"
              onClick={handleClose}
              className="w-full py-2 bg-blue-600 text-white rounded font-medium text-sm hover:bg-blue-700"
            >
              Concluir
            </button>
          </FormDialogBody>
        ) : (
          <form onSubmit={(e) => void handleSubmit(e)} aria-busy={submitting}>
            <FormDialogBody className="space-y-4">
            {error && (
              <div className="p-2 bg-red-50 text-red-600 text-sm rounded border border-red-200" role="alert">
                <p>{error}</p>
                {error === SERVER_CREATE_ERROR && (
                  <button
                    type="button"
                    onClick={() => void handleSubmit()}
                    disabled={submitting}
                    className="mt-2 text-sm font-medium underline"
                  >
                    Tentar novamente
                  </button>
                )}
              </div>
            )}

            <div>
              <label htmlFor="input-username" className="block text-sm font-medium text-slate-700 mb-1">
                Nome de Usuário{isEdit && <span className="text-slate-400 font-normal"> (não editável)</span>}
              </label>
              <input
                id="input-username"
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
                disabled={submitting || isEdit}
                className="w-full p-2 border border-slate-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none disabled:opacity-60"
              />
            </div>

            <div>
              <label htmlFor="input-displayname" className="block text-sm font-medium text-slate-700 mb-1">Nome de Exibição</label>
              <input
                id="input-displayname"
                type="text"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                required
                disabled={submitting}
                className="w-full p-2 border border-slate-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none disabled:opacity-60"
              />
            </div>

            <div>
              <label htmlFor="input-email" className="block text-sm font-medium text-slate-700 mb-1">E-mail (opcional)</label>
              <input
                id="input-email"
                type="email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  setEmailError(null);
                }}
                disabled={submitting}
                className="w-full p-2 border border-slate-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none disabled:opacity-60"
              />
              {emailError && (
                <p className="mt-1 text-sm text-red-600" role="alert">{emailError}</p>
              )}
            </div>

            <div>
              <label htmlFor="select-role" className="block text-sm font-medium text-slate-700 mb-1">Perfil</label>
              <CampoSelect
                id="select-role"
                value={roleId}
                onValueChange={setRoleId}
                disabled={rolesLoading || submitting || roles.length === 0}
                placeholder={rolesLoading ? 'Carregando perfis...' : 'Selecione o perfil'}
                options={roles.map((role) => ({ value: role.id, label: role.name }))}
              />
              {isEdit && editUser && roleId.length > 0 && roleId !== editUser.role.id && (
                <p className="mt-2 rounded border border-border bg-muted p-2 text-sm text-foreground" role="status">
                  {avisoTrocaDePerfil(
                    editUser.displayName || editUser.username,
                    roles.find((role) => role.id === roleId)?.name ?? 'escolhido',
                  )}
                </p>
              )}
            </div>

            </FormDialogBody>
            <FormDialogFooter>
              <button
                type="button"
                onClick={handleClose}
                disabled={submitting}
                className="px-4 py-2 text-slate-600 border border-slate-300 rounded text-sm hover:bg-slate-50 disabled:opacity-50"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={submitting || rolesLoading || roleId.length === 0}
                className="px-4 py-2 bg-blue-600 text-white rounded text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
              >
                {submitting ? (isEdit ? 'Salvando...' : 'Criando...') : isEdit ? 'Salvar' : 'Criar Usuário'}
              </button>
            </FormDialogFooter>
          </form>
        )}
      </FormDialogContent>
    </FormDialog>
  );
}
